import { useEffect, useRef, type ReactNode } from "react";
import { errorMessage } from "../api/client";

export const STATUS_LABELS: Record<string, string> = {
  backlog: "Backlog",
  ready: "Prêt",
  in_progress: "En cours",
  in_review: "En revue",
  done: "Terminé",
  cancelled: "Annulé",
};

const STATUS_TONE: Record<string, string> = {
  ready: "info",
  in_progress: "accent",
  in_review: "warn",
  done: "ok",
  cancelled: "",
  accepted: "ok",
  proposed: "info",
  rejected: "danger",
  deprecated: "",
};

export function Badge({ tone = "", children, title }: { tone?: string; children: ReactNode; title?: string }) {
  return (
    <span className={`badge ${tone}`} title={title}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONE[status] ?? ""}>{STATUS_LABELS[status] ?? status}</Badge>;
}

export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/[\s._/-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
  return <span className="avatar" title={name}>{initials || "?"}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return (
    <div className="empty" role="status">
      Chargement…
    </div>
  );
}

/** Erreur annoncée aux lecteurs d'écran dès qu'elle apparaît (PB-108). */
export function ErrorBox({ error }: { error: unknown }) {
  return (
    <div className="error" role="alert">
      {errorMessage(error)}
    </div>
  );
}

/** Titre de l'onglet du navigateur : chaque page a le sien, lu en premier par les lecteurs d'écran (PB-108). */
export function usePageTitle(...parts: (string | undefined)[]) {
  const title = [...parts.filter(Boolean), "ProjectBrain"].join(" · ");
  useEffect(() => {
    document.title = title;
  }, [title]);
}

const relative = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  return "à l'instant";
}

/** Durée écoulée en court (« 12 min », « 3 h », « 2 j ») : pour les colonnes étroites d'un fil (PB-116). */
export function shortAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (60 * 24))} j`;
}

export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export function date(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";
}

/** Résumé sur une ligne : seul le `code` inline est interprété. */
export function Inline({ children }: { children: string }) {
  return <>{children.split(/(`[^`]+`)/).map((part, i) => (part.startsWith("`") && part.endsWith("`") && part.length > 2 ? <code key={i}>{part.slice(1, -1)}</code> : part))}</>;
}

/**
 * Panneau qui glisse par-dessus la page (PB-116) : le détail d'une tâche ou d'une fiche s'ouvre sans écraser le board ni la
 * liste. Fermé par son bouton, par Échap ou par un clic à côté ; le focus y passe à l'ouverture, une seule fois.
 */
export function SlideOver({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      // Un dialogue ouvert par-dessus (useDialog) traite Échap lui-même.
      if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector(".modal-backdrop")) close.current();
    };
    // Un clic hors du panneau le ferme sans être bloqué : il atteint aussi sa cible (une autre tâche, un lien du menu).
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panel.current && !panel.current.contains(target) && !document.querySelector(".modal-backdrop")?.contains(target)) close.current();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, []);
  return (
    <>
      <div className="slideover-backdrop" aria-hidden="true" />
      <aside ref={panel} tabIndex={-1} className="card drawer slideover" aria-label={label} role="dialog">
        <div className="slideover-head">
          <button className="ghost small" onClick={() => close.current()}>
            Fermer
          </button>
        </div>
        {children}
      </aside>
    </>
  );
}
